/**
 * SearchInput - Expandable search with icon button
 *
 * Core component: No internal dependencies.
 * Starts as circular icon, animates to specified width on click.
 *
 * Multi-search: Use semicolon (;) to search multiple terms.
 * Example: "john;150" searches for "john" AND "150"
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchInputProps {
  /** Current search value (raw string) */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Callback with parsed search terms (split by semicolon) */
  onSearchTerms?: (terms: string[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Expanded width in pixels */
  expandedWidth?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

/** Parse search string into terms (split by semicolon, trim whitespace) */
export function parseSearchTerms(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(';')
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

export function SearchInput({
  value,
  onChange,
  onSearchTerms,
  placeholder = 'Search...',
  expandedWidth = 200,
  disabled = false,
  className,
}: SearchInputProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse and notify search terms when value changes
  const searchTerms = useMemo(() => parseSearchTerms(value), [value]);

  useEffect(() => {
    onSearchTerms?.(searchTerms);
  }, [searchTerms, onSearchTerms]);

  // Auto-expand if there's a value
  useEffect(() => {
    if (value) {
      setIsExpanded(true);
    }
  }, [value]);

  const handleExpand = () => {
    if (disabled) return;
    setIsExpanded(true);
    // Focus after animation starts
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    // Collapse if empty
    if (!value) {
      setIsExpanded(false);
    }
  };

  // Collapsed width (just the icon)
  const collapsedWidth = 48;

  return (
    <div
      style={{
        width: isExpanded ? expandedWidth : collapsedWidth,
        minWidth: isExpanded ? expandedWidth : collapsedWidth,
      }}
      className={cn(
        'relative flex items-center rounded-full bg-white overflow-hidden',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'transition-all duration-300 ease-out',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {/* Search icon / button */}
      <button
        type="button"
        onClick={handleExpand}
        disabled={disabled || isExpanded}
        className={cn(
          'flex-shrink-0 p-3 rounded-full text-slate-500',
          'focus:outline-none',
          !isExpanded && 'active:bg-slate-50'
        )}
        aria-label="Open search"
      >
        <Search className="h-5 w-5" />
      </button>

      {/* Input - appears with animation */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'flex-1 min-w-0 py-3 pr-10 bg-transparent',
          'text-sm text-slate-700 placeholder:text-slate-400',
          'focus:outline-none',
          'transition-opacity duration-200',
          isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      />

      {/* Clear button */}
      <button
        type="button"
        onClick={handleClear}
        className={cn(
          'absolute right-3 p-1 text-slate-400 hover:text-slate-600',
          'transition-all duration-200',
          isExpanded && value ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        aria-label="Clear search"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
